import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { FormDialog } from "@/components/common/FormDialog";
import { dateFmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const MOD_COLOR = { pm: "bg-blue-500", crm: "bg-violet-500", hrms: "bg-amber-500", company: "bg-emerald-500" };
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function MiniCalendarWidget() {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({ queryKey: ["calendar"], queryFn: () => get("/calendar") });
  const create = useMutation({
    mutationFn: (b) => post("/calendar", b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); toast.success("Slot booked"); },
  });

  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const first = new Date(cursor.year, cursor.month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsFor = (day) => events.filter((e) => {
    const ed = new Date(e.date);
    return ed.getDate() === day && ed.getMonth() === cursor.month && ed.getFullYear() === cursor.year;
  });

  const isoFor = (day) => new Date(cursor.year, cursor.month, day, 10, 0, 0).toISOString();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between px-1">
        <button onClick={() => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 })}
          className="rounded p-1 text-muted-foreground hover:bg-muted">‹</button>
        <span className="text-sm font-semibold">{first.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
        <button onClick={() => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 })}
          className="rounded p-1 text-muted-foreground hover:bg-muted">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 px-1">
        {DAYS.map((d, i) => <div key={i} className="text-center text-[10px] font-medium uppercase text-muted-foreground">{d}</div>)}
        {cells.map((day, i) => {
          const evs = day ? eventsFor(day) : [];
          const isToday = day === now.getDate() && cursor.month === now.getMonth() && cursor.year === now.getFullYear();
          if (!day) return <div key={i} />;
          return (
            <FormDialog key={i} title={`Book a slot — ${dateFmt(isoFor(day))}`} testid={`book-slot-${day}`}
              trigger={
                <button data-testid={`cal-day-${day}`}
                  className={cn("flex aspect-square w-full flex-col items-center justify-center rounded-lg text-xs hover:bg-muted",
                    isToday && "bg-primary font-semibold text-primary-foreground hover:bg-primary")}>
                  {day}
                  {evs.length > 0 && (
                    <span className="mt-0.5 flex gap-0.5">
                      {evs.slice(0, 3).map((e, j) => <span key={j} className={cn("h-1 w-1 rounded-full", MOD_COLOR[e.module] || "bg-slate-400")} />)}
                    </span>
                  )}
                </button>
              }
              initial={{ date: `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`, module: "crm" }}
              fields={[
                { name: "title", label: "Title", required: true, full: true },
                { name: "date", label: "Date", type: "date", required: true },
                { name: "start", label: "Start", placeholder: "10:00" },
                { name: "end", label: "End", placeholder: "11:00" },
                { name: "module", label: "Category", type: "select", options: [{ value: "crm", label: "Sales" }, { value: "pm", label: "Project" }, { value: "hrms", label: "HR" }, { value: "company", label: "Company" }] },
              ]}
              onSubmit={(v) => create.mutateAsync({ ...v, date: new Date(v.date).toISOString(), type: "meeting" })} />
          );
        })}
      </div>
      <div className="mt-3 space-y-1.5 border-t border-border pt-3">
        {events.filter((e) => new Date(e.date) >= now).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 3).map((e) => (
          <div key={e.id} className="flex items-center gap-2 text-xs">
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", MOD_COLOR[e.module] || "bg-slate-400")} />
            <span className="flex-1 truncate text-foreground">{e.title}</span>
            <span className="text-muted-foreground">{dateFmt(e.date)}</span>
          </div>
        ))}
        {events.filter((e) => new Date(e.date) >= now).length === 0 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarPlus className="h-3.5 w-3.5" />No upcoming meetings — click a day to book one.</div>
        )}
      </div>
    </div>
  );
}
